import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import FreeExtrasGift from "@/components/account/FreeExtrasGift";
import BillingPaymentMethod from "@/components/account/BillingPaymentMethod";
import { FREE_EXTRAS_COPY, subscriptionForGiftDisplay, validFreeExtrasGift } from "@/lib/free-extras";
import { hasRetainedPaidSubscription } from "@/lib/billing-recovery";
import SignedInSettingsShell from "@/components/shared/SignedInSettingsShell";
import { pricingPlans } from "@/components/landing/pricing-data";
import {
  cancelBillingSubscription,
  clearScheduledDowngrade,
  createCheckoutSession,
  downgradeBillingSubscription,
  getBillingSummary,
  getBillingSubscription,
  getCurrentAccountId,
  listMyAccounts,
  setCurrentAccountId,
  startBillingCheckout,
  submitPayFastPaymentForm,
  trackEvent,
} from "@/lib/api";
import { buildPaidConversionPayload } from "@/lib/billing-analytics";
import {
  redirectToAuthIfRequired,
  redirectToAuthIfSignedOut,
} from "@/lib/auth-required-redirect";
import {
  getBillingStatusBanner,
  getPaidConversionStorageKey,
  getPayfastMerchantNotice,
  shouldShowPayfastMerchantNotice,
} from "@/lib/billing-flow";
import { useI18n } from "@/lib/i18n";
import { resolveOwnedAccountId } from "@/lib/owned-account-selection";
import { EXISTING_PREFERENCES_PATH } from "@/lib/preferences-route";
import { getPlanLabel, normalizeCheckoutPlan, planIndex } from "@/lib/plans";
import { formatSettingsPlanSummary } from "@/lib/settings-shell";

import {
  formatMoney,
  formatDate,
  formatBillingCycleLabel,
  normalizeBillingCycle,
  formatOfferExpiry,
  formatSubscriptionStatusLabel,
  buildCheckoutPriceCatalog,
  mergeCheckoutPriceCatalogs,
  resolveCheckoutPrice,
  hasAnnualCheckoutOption,
  formatCheckoutOptionLabel,
  toBillingSummaryPayload
} from "@/lib/billing-presentation";

export default function Billing() {
  const { locale, tx } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [actionPlan, setActionPlan] = useState("");
  const [cancelErrorMessage, setCancelErrorMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedOfferCadence, setSelectedOfferCadence] = useState("monthly");

  const queryAccountId = searchParams.get("accountId");
  const [resolvedAccountId, setResolvedAccountId] = useState("");
  const accountId = resolvedAccountId;
  const cachedInitialSummary = null;
  const cachedInitialBilling = null;
  const [billingSummary, setBillingSummary] = useState(cachedInitialSummary);
  const [billing, setBilling] = useState(cachedInitialBilling);
  const [billingRecovery, setBillingRecovery] = useState(null);
  const [accountResolving, setAccountResolving] = useState(true);
  const [accountResolutionError, setAccountResolutionError] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(Boolean(accountId) && !cachedInitialSummary);
  const [detailsLoading, setDetailsLoading] = useState(Boolean(accountId) && !cachedInitialBilling);
  const selectedPlan = searchParams.get("plan")
    ? normalizeCheckoutPlan(searchParams.get("plan"), "fun")
    : "";
  const source = searchParams.get("source") || "billing_page";
  const returnTo = searchParams.get("returnTo") === "/agent" ? "/agent" : "";
  const payment = searchParams.get("payment") || "";
  const transactionId = searchParams.get("transactionId") || "";

  useEffect(() => {
    let cancelled = false;
    setAccountResolving(true);
    setAccountResolutionError("");
    setResolvedAccountId("");

    async function resolveBillingAccount() {
      try {
        if (await redirectToAuthIfSignedOut({ navigate, location })) {
          return;
        }
        const payload = await listMyAccounts({ forceFresh: true });
        if (cancelled) return;
        const accounts = Array.isArray(payload?.accounts) ? payload.accounts : [];
        const nextAccountId = resolveOwnedAccountId(accounts, {
          queryAccountId,
          storedAccountId: getCurrentAccountId(),
          primaryAccountId: payload?.billingAccountId || payload?.primaryAccountId,
          fallbackAccountId: accounts[0]?.id,
        });
        if (nextAccountId) {
          setCurrentAccountId(nextAccountId);
          setResolvedAccountId(nextAccountId);
        }
      } catch (error) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : "Could not load your account.";
          if (redirectToAuthIfRequired(error, { navigate, location })) {
            return;
          }
          if (/no linked account found/i.test(message)) {
            setResolvedAccountId("");
            return;
          }
          setAccountResolutionError(message);
        }
      } finally {
        if (!cancelled) setAccountResolving(false);
      }
    }

    void resolveBillingAccount();

    return () => {
      cancelled = true;
    };
  }, [location, navigate, queryAccountId]);

  useEffect(() => {
    if (!accountId) {
      setBillingSummary(null);
      setBilling(null);
      setSummaryLoading(false);
      setDetailsLoading(false);
      return;
    }
    let cancelled = false;
    setBillingSummary((current) => (current?.accountId === accountId ? current : null));
    setSummaryLoading(true);
    setErrorMessage("");
    getBillingSummary(accountId, { forceFresh: true })
      .then((payload) => {
        if (!cancelled) {
          setBillingSummary(payload);
          setErrorMessage("");
        }
      })
      .catch((error) => {
        if (cancelled || redirectToAuthIfRequired(error, { navigate, location })) {
          return;
        }
        setErrorMessage(error.message);
      })
      .finally(() => {
        if (!cancelled) {
          setSummaryLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, location, navigate, payment, transactionId]);

  useEffect(() => {
    if (!accountId) {
      return;
    }
    let cancelled = false;
    setBilling((current) => (current?.accountId === accountId ? current : null));
    setDetailsLoading(true);
    getBillingSubscription(accountId, { forceFresh: true })
      .then((payload) => {
        if (!cancelled) {
          setBilling(payload);
          setBillingSummary(toBillingSummaryPayload(payload));
          setErrorMessage("");
        }
      })
      .catch((error) => {
        if (cancelled || redirectToAuthIfRequired(error, { navigate, location })) {
          return;
        }
        setErrorMessage(error.message);
      })
      .finally(() => {
        if (!cancelled) {
          setDetailsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, location, navigate, payment, transactionId]);

  useEffect(() => {
    if (!billing || payment !== "complete" || !transactionId || typeof window === "undefined") {
      return;
    }
    const key = getPaidConversionStorageKey(transactionId);
    if (window.sessionStorage.getItem(key)) {
      return;
    }
    const metadata = billing.latestTransaction?.metadata || {};
    trackEvent("paid_conversion", buildPaidConversionPayload({
      fromPlan: metadata.previous_plan || "free",
      toPlan: metadata.plan_code || billing.currentPlan || "free",
      metadata,
    }));
    window.sessionStorage.setItem(key, "1");
  }, [billing, payment, transactionId]);

  useEffect(() => {
    if (!billing || payment !== "pending" || !transactionId || !accountId) {
      return;
    }
    const latestTransaction = billing.latestTransaction;
    if (latestTransaction?.id === transactionId) {
      if (latestTransaction.status === "complete") {
        navigate(
          `/billing?payment=complete&transactionId=${encodeURIComponent(transactionId)}&accountId=${encodeURIComponent(accountId)}${returnTo ? "&returnTo=%2Fagent" : ""}`,
          { replace: true },
        );
        return;
      }
      if (latestTransaction.status === "failed" || latestTransaction.status === "cancelled") {
        navigate(
          `/billing?payment=${encodeURIComponent(latestTransaction.status)}&transactionId=${encodeURIComponent(transactionId)}&accountId=${encodeURIComponent(accountId)}${returnTo ? "&returnTo=%2Fagent" : ""}`,
          { replace: true },
        );
        return;
      }
    }
    const timeoutId = window.setTimeout(() => {
      refreshBilling({ forceFresh: true }).catch(() => null);
    }, 3000);
    return () => window.clearTimeout(timeoutId);
  }, [accountId, billing, navigate, payment, transactionId, returnTo]);

  const planCards = useMemo(
    () => pricingPlans.filter((plan) => plan.slug !== "free"),
    [],
  );

  const activeSummary = useMemo(
    () => billingSummary || toBillingSummaryPayload(billing),
    [billing, billingSummary],
  );
  const currentPlan = activeSummary?.currentPlan || billing?.currentPlan || "free";
  const currentPlanLabel = getPlanLabel(currentPlan);
  const freeExtrasGift = validFreeExtrasGift(activeSummary?.freeExtrasGift);
  const activeSubscription = subscriptionForGiftDisplay(activeSummary?.subscription || billing?.subscription, freeExtrasGift);
  const retainedPaidSubscription = hasRetainedPaidSubscription(activeSubscription);
  const providerCancelled = billingRecovery?.accountId === accountId && billingRecovery.status === "cancelled";
  const freeExtrasCopy = FREE_EXTRAS_COPY[locale];
  const activeOffer = billing?.activeOffer || null;
  const activeOfferPlans = Array.isArray(billing?.activeOfferPlans) ? billing.activeOfferPlans : [];
  const normalizedSubscriptionStatus = String(activeSubscription?.status || "").trim().toLowerCase();
  const planSummaryLoading = summaryLoading && !activeSummary;
  const detailPayloadLoading = detailsLoading && !billing;
  const banner = getBillingStatusBanner(payment);
  const payfastMerchantNotice = getPayfastMerchantNotice();
  const showPayfastMerchantNotice = shouldShowPayfastMerchantNotice(selectedPlan);
  const renewalDateLabel = activeSubscription?.currentPeriodEnd
    ? formatDate(activeSubscription.currentPeriodEnd)
    : null;
  const pendingChangeDateLabel = activeSubscription?.pendingChangeAt
    ? formatDate(activeSubscription.pendingChangeAt)
    : null;
  const renewalDateFallbackLabel = "the end of your current billing period";
  const priceLabel = activeSubscription
    ? formatMoney(activeSubscription.priceAmountCents, activeSubscription.priceCurrency)
    : null;
  const cadenceLabel = activeSubscription
    ? formatBillingCycleLabel(activeSubscription.billingCycle)
    : null;
  const renewalPriceLabel = priceLabel
    ? `${priceLabel} / ${cadenceLabel}`
    : activeSubscription
      ? "Active"
      : "No charge";
  const billingStatusLabel = providerCancelled ? "Auto-renewal is off" : retainedPaidSubscription ? "Payment needs attention" : !activeSubscription && freeExtrasGift
    ? freeExtrasCopy.giftTitle : formatSubscriptionStatusLabel(activeSubscription);
  const coverageLabel = currentPlan === "free" && !retainedPaidSubscription
    ? "Upgrade any linked number from here"
    : "Shared across linked numbers";
  const hasPendingPlanChange = Boolean(activeSubscription?.pendingPlanCode);
  const hasScheduledPaidPlanChange = Boolean(
    activeSubscription?.pendingPlanCode
      && activeSubscription.pendingPlanCode !== "free"
  );
  const currentPlanWillRenew = Boolean(
    activeSubscription
      && normalizedSubscriptionStatus === "active"
      && !activeSubscription.cancelAtPeriodEnd
      && !hasScheduledPaidPlanChange,
  );
  const accessEndLabel = activeSubscription
    ? renewalDateLabel || renewalDateFallbackLabel
    : "No renewal scheduled";
  const cancelledPlanCopy = renewalDateLabel
    ? `${currentPlanLabel} stays active until ${renewalDateLabel}.`
    : `${currentPlanLabel} stays active until ${renewalDateFallbackLabel}.`;
  const summaryTimingLabel = retainedPaidSubscription
    ? "Paid access ended"
    : !activeSubscription
    ? "Current charge"
    : currentPlanWillRenew
      ? "Next charge"
      : "Access until";
  const summaryTimingValue = !activeSubscription
    ? "No charge"
    : currentPlanWillRenew
      ? renewalPriceLabel
      : accessEndLabel;
  const summaryTimingCopy = retainedPaidSubscription
    ? ""
    : activeSubscription
    ? hasScheduledPaidPlanChange
      ? pendingChangeDateLabel
        ? `Switches to ${getPlanLabel(activeSubscription.pendingPlanCode)} on ${pendingChangeDateLabel}.`
        : `Switches to ${getPlanLabel(activeSubscription.pendingPlanCode)} at ${renewalDateFallbackLabel}.`
      : currentPlanWillRenew
        ? renewalDateLabel
          ? `Renews on ${renewalDateLabel}`
          : "Renewal date pending"
        : renewalDateLabel || cancelledPlanCopy
    : "Billing starts once you activate a paid plan.";
  const renewalFactLabel = retainedPaidSubscription
    ? "Paid access ended"
    : !activeSubscription
    ? "Billing starts"
    : currentPlanWillRenew
      ? "Renews on"
      : "Access ends";
  const renewalFactValue = !activeSubscription
    ? "When you choose a paid plan"
    : renewalDateLabel || (currentPlanWillRenew ? "Renewal date pending" : accessEndLabel);
  const scheduledChangeCopy = pendingChangeDateLabel
    ? `Switches to ${getPlanLabel(activeSubscription?.pendingPlanCode)} on ${pendingChangeDateLabel}.`
    : `Switches to ${getPlanLabel(activeSubscription?.pendingPlanCode)} at ${renewalDateFallbackLabel}.`;
  const stopRenewalCopy = retainedPaidSubscription
    ? "This stops future payments for your existing subscription."
    : renewalDateLabel
    ? `${currentPlanLabel} stays active until ${renewalDateLabel}. After that, your account drops back to Free unless you start a new paid plan.`
    : `${currentPlanLabel} stays active until ${renewalDateFallbackLabel}. After that, your account drops back to Free unless you start a new paid plan.`;
  const standardCheckoutPriceCatalog = useMemo(
    () => buildCheckoutPriceCatalog(billing?.plans || []),
    [billing?.plans],
  );
  const activeOfferCheckoutPriceCatalog = useMemo(
    () => buildCheckoutPriceCatalog(activeOfferPlans),
    [activeOfferPlans],
  );
  const effectiveCheckoutPriceCatalog = useMemo(
    () => mergeCheckoutPriceCatalogs(standardCheckoutPriceCatalog, activeOfferCheckoutPriceCatalog),
    [activeOfferCheckoutPriceCatalog, standardCheckoutPriceCatalog],
  );
  const effectiveCheckoutOfferType = String(activeOffer?.offerType || "standard").trim() || "standard";
  const activeOfferExpiryLabel = formatOfferExpiry(activeOffer?.expiresAt);
  const showWinbackOfferBanner = effectiveCheckoutOfferType === "winback_drop_24h";
  const showFooterOfferBanner = effectiveCheckoutOfferType === "footer_surprise_24h";
  const showAnnualWinbackOption = showWinbackOfferBanner && hasAnnualCheckoutOption(activeOfferCheckoutPriceCatalog);
  const effectiveCheckoutBillingCycle = showAnnualWinbackOption
    ? normalizeBillingCycle(selectedOfferCadence)
    : "monthly";

  useEffect(() => {
    if (!showAnnualWinbackOption) {
      setSelectedOfferCadence("monthly");
      return;
    }
    const selectedPlanCatalog = activeOfferCheckoutPriceCatalog.fun || activeOfferCheckoutPriceCatalog.pro || {};
    if (!resolveCheckoutPrice(selectedPlanCatalog, selectedOfferCadence)) {
      setSelectedOfferCadence(selectedPlanCatalog.annual ? "annual" : "monthly");
    }
  }, [activeOfferCheckoutPriceCatalog, selectedOfferCadence, showAnnualWinbackOption]);

  async function refreshBilling({ forceFresh = false } = {}) {
    if (!accountId) return;
    const payload = await getBillingSubscription(accountId, { forceFresh });
    setBilling(payload);
    setBillingSummary(toBillingSummaryPayload(payload));
  }

  async function handleRefresh() {
    try {
      setActionPlan("refresh");
      setErrorMessage("");
      await refreshBilling({ forceFresh: true });
    } catch (error) {
      if (redirectToAuthIfRequired(error, { navigate, location })) {
        return;
      }
      setErrorMessage(error.message);
    } finally {
      setActionPlan("");
    }
  }

  async function handleCheckout(plan) {
    if (!accountId) {
      createCheckoutSession(plan, { source, fromPlan: currentPlan });
      return;
    }
    try {
      setActionPlan(plan);
      setErrorMessage("");
      const checkoutStartedAtMs =
        typeof performance !== "undefined" && typeof performance.now === "function"
          ? performance.now()
          : Date.now();
      const checkoutPrice = resolveCheckoutPrice(
        effectiveCheckoutPriceCatalog[plan],
        effectiveCheckoutBillingCycle,
      );
      if (!checkoutPrice?.id) {
        throw new Error("No billing price is available yet.");
      }
      const checkout = await startBillingCheckout({
        accountId,
        priceId: checkoutPrice.id,
        toPlan: plan,
        source,
        fromPlan: currentPlan,
        billingCycle: checkoutPrice.billingCycle,
        offerType: effectiveCheckoutOfferType,
        offerId: activeOffer?.id || "",
        returnTo,
        checkoutStartedAtMs,
      });
      submitPayFastPaymentForm(checkout);
    } catch (error) {
      if (redirectToAuthIfRequired(error, { navigate, location })) {
        return;
      }
      setErrorMessage(error.message);
    } finally {
      setActionPlan("");
    }
  }

  async function handleCancel() {
    try {
      setActionPlan("free");
      setErrorMessage("");
      setCancelErrorMessage("");
      const payload = await cancelBillingSubscription(accountId);
      setBilling(payload);
      setBillingSummary(toBillingSummaryPayload(payload));
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Could not cancel right now.";
      if (redirectToAuthIfRequired(error, { navigate, location })) {
        return false;
      }
      setCancelErrorMessage(message);
      return false;
    } finally {
      setActionPlan("");
    }
  }

  async function handleDowngrade(plan) {
    try {
      setActionPlan(plan);
      setErrorMessage("");
      const payload = await downgradeBillingSubscription(accountId, plan);
      setBilling(payload);
      setBillingSummary(toBillingSummaryPayload(payload));
    } catch (error) {
      if (redirectToAuthIfRequired(error, { navigate, location })) {
        return;
      }
      setErrorMessage(error.message);
    } finally {
      setActionPlan("");
    }
  }

  async function handleUndoDowngrade() {
    try {
      setActionPlan("undo");
      setErrorMessage("");
      const payload = await clearScheduledDowngrade(accountId);
      setBilling(payload);
      setBillingSummary(toBillingSummaryPayload(payload));
    } catch (error) {
      if (redirectToAuthIfRequired(error, { navigate, location })) {
        return;
      }
      setErrorMessage(error.message);
    } finally {
      setActionPlan("");
    }
  }

  return (
    <SignedInSettingsShell
      currentSection="billing"
      title="Billing"
      onBack={() => navigate(-1)}
      planLabel={`${currentPlanLabel} plan`}
      planTooltipCopy={formatSettingsPlanSummary(currentPlan, activeSubscription)}
      planTooltipLoading={planSummaryLoading && Boolean(accountId)}
      sectionLinks={{
        preferences: accountId ? `${EXISTING_PREFERENCES_PATH}&accountId=${encodeURIComponent(accountId)}` : EXISTING_PREFERENCES_PATH,
        referrals: accountId ? `/referrals?accountId=${encodeURIComponent(accountId)}` : "/referrals",
        account: accountId ? `/account?accountId=${encodeURIComponent(accountId)}` : "/account",
      }}
    >
        <section className="max-w-2xl max-[559px]:sr-only">
          <h1 className="font-display text-3xl font-bold tracking-tight">{tx("Billing")}</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {tx("Switch plans once for your Min account. Billing is shared across your linked WhatsApp numbers.")}
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            {tx("Plans are shown in USD here. PayFast charges South African rand at checkout.")}
          </p>
        </section>

        {banner && (
          <section
            className={`rounded-2xl border p-4 ${
              banner.tone === "success"
                ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                : banner.tone === "danger"
                  ? "border-red-200 bg-red-50 text-red-900"
                  : "border-border bg-card"
            }`}
          >
            <div className="flex items-start gap-3">
              {banner.tone === "success" ? (
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
              ) : (
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
              )}
              <div>
                <p className="font-semibold">{tx(banner.title)}</p>
                <p className="mt-1 text-sm opacity-90">{tx(banner.copy)}</p>
                {returnTo === "/agent" && currentPlan === "pro" ? (
                  <Button className="mt-3 rounded-full" onClick={() => navigate("/agent")}>{tx("Continue to agent setup")}</Button>
                ) : null}
              </div>
            </div>
          </section>
        )}

        {(accountResolutionError || errorMessage) && (
          <section className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-900">
            {tx(accountResolutionError || errorMessage)}
          </section>
        )}

        {!accountId && accountResolving && !accountResolutionError && (
          <section className="rounded-3xl border bg-card p-6" role="status">
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {tx("Loading billing details...")}
            </div>
          </section>
        )}

        {!accountId && !accountResolving && !accountResolutionError && !planSummaryLoading && (
          <section className="rounded-3xl border bg-card p-6">
            <p className="font-semibold">{tx("Link your WhatsApp account first")}</p>
            <p className="mt-2 text-sm text-muted-foreground">
              {tx("Billing is attached to the linked WhatsApp account that Min manages for you.")}
            </p>
            <Button className="mt-4 h-11 rounded-xl" onClick={() => navigate("/link")}>
              {tx("Link WhatsApp")}
            </Button>
          </section>
        )}

        {accountId && (
          <>
            <BillingPaymentMethod
              accountId={accountId}
              subscription={activeSubscription}
              action={actionPlan}
              setAction={setActionPlan}
              onRecoveryChange={setBillingRecovery}
              onBillingChanged={() => refreshBilling({ forceFresh: true })}
            />

            <section
              className="overflow-hidden rounded-3xl border bg-card"
              data-testid="billing-subscription-summary"
            >
              {planSummaryLoading ? (
                <div className="m-6 h-40 animate-pulse rounded-2xl bg-muted/60" />
              ) : (
                <>
                  <div className="border-b border-border/70 px-6 py-6 sm:px-7">
                    <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                      <div className="min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                          {tx("Plan")}
                        </p>
                        <div className="mt-3 flex flex-wrap items-center gap-3">
                          <h2 className="font-display text-4xl font-bold">{tx(currentPlanLabel)}</h2>
                          {activeSubscription?.status && (currentPlan !== "free" || retainedPaidSubscription) ? (
                            <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium capitalize text-muted-foreground">
                              {tx(providerCancelled ? "Auto-renewal is off" : retainedPaidSubscription ? "Payment needs attention" : activeSubscription.status)}
                            </span>
                          ) : null}
                        </div>
                        <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
                          {activeSubscription
                            ? activeSubscription.cancelAtPeriodEnd
                              ? tx(cancelledPlanCopy)
                              : tx("One shared subscription controls billing across your linked WhatsApp numbers.")
                            : freeExtrasGift ? tx("Shared across linked numbers") : tx("You’re on the free plan right now. Upgrade whenever you need more coverage.")}
                        </p>
                      </div>

                      <div className="rounded-2xl border border-border/70 bg-muted/30 px-4 py-4 lg:min-w-72">
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                          {tx(summaryTimingLabel)}
                        </p>
                        <p className="mt-3 text-2xl font-semibold text-foreground">{tx(summaryTimingValue)}</p>
                        {summaryTimingCopy ? <p className="mt-2 text-sm text-muted-foreground">{tx(summaryTimingCopy)}</p> : null}
                      </div>
                    </div>
                  </div>

                  <div className="px-6 py-5 sm:px-7">
                    {freeExtrasGift ? <div className="mb-4"><FreeExtrasGift gift={freeExtrasGift} /></div> : null}
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                      <article className="rounded-2xl border border-border/70 bg-background px-4 py-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                          {tx(renewalFactLabel)}
                        </p>
                        <p className="mt-2 text-sm font-semibold text-foreground">
                          {tx(renewalFactValue)}
                        </p>
                      </article>

                      <article className="rounded-2xl border border-border/70 bg-background px-4 py-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                          {tx("Amount")}
                        </p>
                        <p className="mt-2 text-sm font-semibold text-foreground">{tx(renewalPriceLabel)}</p>
                      </article>

                      <article className="rounded-2xl border border-border/70 bg-background px-4 py-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                          {tx("Status")}
                        </p>
                        <p className="mt-2 text-sm font-semibold text-foreground">{tx(billingStatusLabel)}</p>
                      </article>

                      <article className="rounded-2xl border border-border/70 bg-background px-4 py-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                          {tx("Coverage")}
                        </p>
                        <p className="mt-2 text-sm font-semibold text-foreground">{tx(coverageLabel)}</p>
                      </article>
                    </div>

                    {hasPendingPlanChange ? (
                      <section className="mt-4 rounded-2xl border border-primary/20 bg-primary/5 px-4 py-4">
                        <p className="text-sm font-semibold text-foreground">{tx("Scheduled change")}</p>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {tx(scheduledChangeCopy)}
                        </p>
                        <Button
                          variant="ghost"
                          className="mt-3 h-10 rounded-xl px-0 text-sm text-primary hover:bg-transparent hover:text-primary"
                          disabled={Boolean(actionPlan)}
                          onClick={handleUndoDowngrade}
                        >
                          {actionPlan === "undo" ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              {tx("Clearing change…")}
                            </>
                          ) : (
                            tx("Keep current plan")
                          )}
                        </Button>
                      </section>
                    ) : null}

                    {!activeSubscription ? (
                      <Button
                        variant="ghost"
                        className="mt-4 h-10 rounded-xl px-0 text-sm"
                        disabled={Boolean(actionPlan)}
                        onClick={handleRefresh}
                      >
                        {actionPlan === "refresh" ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            {tx("Refreshing…")}
                          </>
                        ) : (
                          tx("Refresh billing status")
                        )}
                      </Button>
                    ) : null}
                  </div>
                </>
              )}
            </section>

            <section className="space-y-4">
              <div className="max-w-2xl">
                <h2 className="font-display text-2xl font-semibold text-foreground">{tx("Change plan")}</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {tx("Upgrade immediately, or switch down at the end of your current billing cycle.")}
                </p>
              </div>

              {showFooterOfferBanner ? (
                <div className="rounded-3xl border border-primary/20 bg-primary/5 p-5">
                  <p className="font-semibold">{tx("You have a private first-month offer.")}</p>
                  <p className="mt-2 text-sm">{tx("50% off the first month. Then the normal monthly price.")}</p>
                  {activeOfferExpiryLabel ? <p className="mt-2 text-sm text-muted-foreground">{tx("Offer ends {date}").replaceAll("{date}", activeOfferExpiryLabel)}</p> : null}
                </div>
              ) : null}
              {showWinbackOfferBanner ? (
                <div className="rounded-3xl border border-primary/20 bg-primary/5 p-5">
                  <p className="text-sm font-semibold text-foreground">{tx("Your 24-hour win-back offer is active.")}</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {showAnnualWinbackOption
                      ? tx("Choose monthly 20% off or yearly 40% off below. The discount is applied automatically at checkout from this page.")
                      : tx("The discounted monthly price is applied automatically at checkout from this page.")}
                    {activeOfferExpiryLabel ? ` ${tx(`Ends ${activeOfferExpiryLabel}.`)}` : ""}
                  </p>
                  {showAnnualWinbackOption ? (
                    <div className="mt-4 grid grid-cols-2 gap-2 rounded-2xl bg-background/70 p-1 sm:max-w-sm">
                      {[
                        { value: "monthly", label: "Monthly 20% off" },
                        { value: "annual", label: "Annual 40% off" },
                      ].map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setSelectedOfferCadence(option.value)}
                          disabled={Boolean(actionPlan)}
                          className={`rounded-[1rem] px-4 py-3 text-sm font-semibold transition-colors ${
                            effectiveCheckoutBillingCycle === option.value
                              ? "bg-background text-foreground shadow-sm"
                              : "text-muted-foreground"
                          }`}
                        >
                          {tx(option.label)}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className="grid gap-4 md:grid-cols-3">
                {detailPayloadLoading ? (
                  <div className="rounded-3xl border border-border bg-card p-5 md:col-span-3">
                    <p className="text-sm font-semibold text-foreground">{tx("Loading full billing details")}</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {tx("Your current plan is ready. Checkout options and payment history are loading in the background.")}
                    </p>
                  </div>
                ) : null}
                {showPayfastMerchantNotice ? (
                  <div className="rounded-3xl border border-border bg-card p-5 md:col-span-3">
                    <p className="text-sm font-semibold text-foreground">{tx(payfastMerchantNotice.title)}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{tx(payfastMerchantNotice.copy)}</p>
                  </div>
                ) : null}
                {planCards.map((plan) => {
                  const currentRank = planIndex(currentPlan);
                  const targetRank = planIndex(plan.slug);
                  const isCurrent = currentPlan === plan.slug && activeSubscription?.status === "active";
                  const isRecovery = retainedPaidSubscription && !providerCancelled && activeSubscription.planCode === plan.slug;
                  const isDowngrade = currentPlan !== "free" && targetRank < currentRank;
                  const isSelected = selectedPlan === plan.slug;
                  const selectable = plan.selectable !== false;
                  const checkoutPrice = resolveCheckoutPrice(
                    effectiveCheckoutPriceCatalog[plan.slug],
                    effectiveCheckoutBillingCycle,
                  );
                  const checkoutReady = isRecovery || (!detailPayloadLoading && (isDowngrade || Boolean(checkoutPrice?.id)));
                  const isFirstMonthOffer = showFooterOfferBanner && checkoutPrice?.recurringAmountCents > checkoutPrice?.amountCents;
                  const displayedPrice = isFirstMonthOffer
                    ? tx("{initial} first month, then {recurring}/month")
                      .replaceAll("{initial}", formatMoney(checkoutPrice.amountCents, checkoutPrice.currency))
                      .replaceAll("{recurring}", formatMoney(checkoutPrice.recurringAmountCents, checkoutPrice.currency))
                    : showWinbackOfferBanner
                      ? formatCheckoutOptionLabel(checkoutPrice) || `${plan.price}${plan.period}`
                      : `${plan.price}${plan.period}`;
                  const actionLabel = isRecovery
                    ? "Update card"
                    : detailPayloadLoading
                    ? "Loading…"
                    : isCurrent
                    ? "Current plan"
                    : !selectable
                      ? "Coming soon"
                    : isDowngrade
                      ? "Switch at period end"
                      : effectiveCheckoutBillingCycle === "annual"
                        ? `Start ${plan.name} yearly`
                        : `Start ${plan.name}`;
                  return (
                    <article
                      key={plan.slug}
                      className={`rounded-3xl border bg-card p-6 ${isSelected ? "border-primary shadow-lg shadow-primary/10" : ""}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <h2 className="font-display text-2xl font-bold">{tx(plan.name)}</h2>
                          <p className="mt-1 text-sm text-muted-foreground">
                            {tx(displayedPrice)}
                          </p>
                        </div>
                        {plan.statusLabel ? (
                          <span className="rounded-full border border-border bg-background px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                            {tx(plan.statusLabel)}
                          </span>
                        ) : null}
                        {isSelected && selectable ? (
                          <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                            {tx("Selected")}
                          </span>
                        ) : null}
                      </div>

                      <ul className="mt-5 space-y-2 text-sm text-muted-foreground">
                        {plan.features.slice(0, 4).map((feature) => (
                          <li key={feature.key}>{tx(feature.text)}</li>
                        ))}
                      </ul>

                      <Button
                        asChild={isRecovery}
                        className="mt-6 h-11 w-full rounded-xl"
                        variant={isCurrent ? "outline" : "default"}
                        disabled={isCurrent || Boolean(actionPlan) || !selectable || !checkoutReady}
                        onClick={() => {
                          if (isRecovery) return;
                          if (!selectable) {
                            return;
                          }
                          if (isDowngrade) {
                            handleDowngrade(plan.slug);
                            return;
                          }
                          handleCheckout(plan.slug);
                        }}
                      >
                        {isRecovery ? <a href="#payment-method">{tx("Update card")}</a> : actionPlan === plan.slug ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            {isDowngrade ? tx("Scheduling change…") : tx("Opening checkout…")}
                          </>
                        ) : (
                          tx(actionLabel)
                        )}
                      </Button>
                    </article>
                  );
                })}
              </div>
            </section>

            {activeSubscription && !providerCancelled && (currentPlan !== "free" || retainedPaidSubscription) ? (
              <section
                className="rounded-3xl border border-destructive/20 bg-destructive/[0.03] p-5 sm:p-6"
                data-testid="billing-renewal-controls"
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="max-w-2xl">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-destructive/80">
                      {tx("Renewal controls")}
                    </p>
                    <h2 className="mt-2 font-display text-2xl font-semibold text-foreground">
                      {activeSubscription.cancelAtPeriodEnd ? tx("Auto-renewal is off") : tx("Stop auto-renewal")}
                    </h2>
                    <p id="stop-renewal-terms" className="mt-2 text-sm text-muted-foreground">
                      {activeSubscription.cancelAtPeriodEnd
                        ? tx(cancelledPlanCopy)
                        : tx(stopRenewalCopy)}
                    </p>
                  </div>

                  {!activeSubscription.cancelAtPeriodEnd ? (
                    <div className="flex flex-col gap-2">
                      <Button
                        variant="outline"
                        className="h-11 rounded-xl border-destructive/25 text-destructive hover:bg-destructive/5 hover:text-destructive"
                        disabled={Boolean(actionPlan)}
                        aria-describedby="stop-renewal-terms"
                        onClick={() => void handleCancel()}
                      >
                        {actionPlan === "free" ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
                        {tx(actionPlan === "free" ? "Stopping renewal…" : "Stop auto-renewal")}
                      </Button>
                      {cancelErrorMessage ? <p className="text-sm text-destructive" role="alert">{tx(cancelErrorMessage)}</p> : null}
                    </div>
                  ) : null}
                </div>
              </section>
            ) : null}
          </>
        )}
    </SignedInSettingsShell>
  );
}
